/**
 * Everything we know about the machine the game is running on.
 *
 * Only collected when the player leaves "Share System Information" checked, and
 * only ever sent inside their own report - see the notice in the report modal.
 */

export type SystemInfo = Record<string, any>;

function webglInfo(): SystemInfo {
	const result: SystemInfo = {};
	let canvas: HTMLCanvasElement | null = null;
	try {
		canvas = document.createElement('canvas');
		const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl')) as WebGLRenderingContext | null;
		if (!gl) {
			result.available = false;
			return result;
		}
		result.available = true;
		result.version = gl.getParameter(gl.VERSION);
		result.shadingLanguageVersion = gl.getParameter(gl.SHADING_LANGUAGE_VERSION);
		result.vendor = gl.getParameter(gl.VENDOR);
		result.renderer = gl.getParameter(gl.RENDERER);
		result.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);

		// The masked vendor/renderer above are generic ("WebKit WebGL"); the real
		// GPU and driver string only come from this debug extension.
		const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
		if (debugInfo) {
			result.unmaskedVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
			result.unmaskedRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
		}

		// Release the context rather than waiting for GC - browsers only allow a
		// handful at once and the game needs its own.
		gl.getExtension('WEBGL_lose_context')?.loseContext();
	} catch (e: any) {
		result.error = String(e?.message ?? e);
	}
	return result;
}

function browserInfo(): SystemInfo {
	const nav = navigator as any;
	const info: SystemInfo = {
		userAgent: navigator.userAgent,
		language: navigator.language,
		platform: navigator.platform,
		hardwareConcurrency: nav.hardwareConcurrency ?? null,
		deviceMemoryGB: nav.deviceMemory ?? null,
		online: navigator.onLine,
		screen: {
			width: window.screen?.width,
			height: window.screen?.height,
			availWidth: window.screen?.availWidth,
			availHeight: window.screen?.availHeight,
			colorDepth: window.screen?.colorDepth,
			devicePixelRatio: window.devicePixelRatio,
		},
		window: {
			innerWidth: window.innerWidth,
			innerHeight: window.innerHeight,
		},
	};

	if (nav.userAgentData) {
		info.userAgentData = {
			brands: nav.userAgentData.brands,
			mobile: nav.userAgentData.mobile,
			platform: nav.userAgentData.platform,
		};
	}

	// Chrome only, and only a rough number, but it's the one signal we get for
	// "did this player run out of JS heap".
	const memory = (performance as any).memory;
	if (memory) {
		info.jsHeap = {
			usedMB: Math.round(memory.usedJSHeapSize / 1048576),
			totalMB: Math.round(memory.totalJSHeapSize / 1048576),
			limitMB: Math.round(memory.jsHeapSizeLimit / 1048576),
		};
	}

	return info;
}

/**
 * Gathers system info. The Electron main process knows far more than the page
 * does (real GPU driver, CPU model, system memory), so pull that in when we can.
 */
export async function collectSystemInfo(): Promise<SystemInfo> {
	const info: SystemInfo = {
		browser: browserInfo(),
		webgl: webglInfo(),
	};

	const electron = (window as any).electronApi?.bugReport;
	if (electron?.getSystemInfo) {
		try {
			info.electron = await electron.getSystemInfo();
		} catch (e: any) {
			info.electron = { error: String(e?.message ?? e) };
		}
	}

	return info;
}
